import MUIBox from '@/components/MUI-Components/MUI-Box';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import DoneIcon from '@mui/icons-material/Done';
import {memo} from 'react';
import parse from 'html-react-parser';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import {borderRadius, colors, fontFamily, fontSize, fontWeight} from '@/styles/tokens';

// Migrated from globals.scss `.pricing-background` (+ `:hover`), `.pricing-button`,
// `.icon-background`, and the `.spectrum-header-wrapper .pricing-*` descendant rules.
// `.spectrum-heading` / `.spectrum-header-wrapper` base rules and `.solution-sub-heading`
// stay global — shared beyond this component (Batch H cluster / 3 other components).
// `.hire-pricing .hire-pricing-sub-heading` also stays global — 28 of 32 consuming pages
// opt in, 3 intentionally don't, so it's not a safe component default (playbook blocker B-5).
// Also migrated: the `@include ipadScreen` (max-width:992px) `.pricing-background:hover`
// override (sole consumer) — folded into the `&:hover` block below as a nested media query.
// `.price-dot-net-card .pricing-background` and `.remote-pricing-developer-card
// .pricing-background` overrides stay global — those are page-authored ancestor classes,
// not a DOM node this component owns.
const pricingBackgroundSx: SxProps<Theme> = {
    backgroundColor: colors.background.default,
    borderRadius: borderRadius['2xl'],
    position: 'relative',
    top: '2.5rem',
    transition: 'all 0.5s ease-in-out 0s',
    boxShadow: '0px 0px 11px 0px rgba(0, 0, 0, 0.1)', // no exact token match — preserved for parity
    height: '100% !important',
    '&:hover': {
        background: `linear-gradient(90deg, ${colors.primary.main}, ${colors.primary.midnight})`,
        top: '0rem',
        boxShadow: '0px 0px 0px 0px',
        '@media (max-width: 992px)': {
            backgroundColor: '#625bb7 !important', // no exact token match — preserved for parity
            top: '2.5rem'
        },
        '.pricing-button': {
            backgroundColor: `${colors.background.default} !important`,
            border: `1px solid ${colors.background.default} !important`,
            color: `${colors.primary.main} !important`
        },
        '.spectrum-header-wrapper .pricing-heading, .spectrum-header-wrapper .pricing-sub-heading, .spectrum-header-wrapper .pricing-duration':
            {
                color: `${colors.text.inverse} !important`
            },
        '.spectrum-header-wrapper .pricing-sub-heading a': {
            color: `${colors.text.inverse} !important`,
            textDecoration: 'underline !important'
        },
        '.icon-background': {
            backgroundColor: `${colors.background.default} !important`,
            color: `${colors.primary.main} !important`
        }
    }
};

const pricingButtonSx: SxProps<Theme> = {
    boxShadow: 'none !important',
    fontFamily: `${fontFamily.heading} !important`,
    borderRadius: `${borderRadius.lg} !important`,
    backgroundColor: `${colors.primary.main} !important`,
    color: colors.text.inverse,
    textTransform: 'capitalize !important',
    padding: '10px 30px !important',
    border: `1px solid ${colors.primary.main} !important`,
    fontSize: `${fontSize[16]} !important`
};

const iconBackgroundSx: SxProps<Theme> = {
    backgroundColor: `${colors.primary.main} !important`,
    borderRadius: `${borderRadius.circle} !important`,
    padding: '4px !important',
    color: `${colors.text.inverse} !important`,
    marginRight: '4px !important',
    marginTop: '4px !important',
    width: '0.75em !important',
    height: '0.75em !important'
};

const pricingSubHeadingSx = {
    fontSize: `${fontSize[16]} !important`,
    fontFamily: `${fontFamily.body} !important`,
    textAlign: 'left !important',
    '& span': {
        fontWeight: `${fontWeight.bold} !important`
    }
} as const;

const pricingHeadingWrapperSx: SxProps<Theme> = {
    '.pricing-heading': {fontSize: `${fontSize[22]} !important`},
    '.pricing-sub-heading': pricingSubHeadingSx
};

// Migrated from globals.scss `.flex` and `.text-justify` (both sole consumers) — this
// wrapper is the only element in the codebase carrying both classNames together.
const pricingPointsWrapperSx: SxProps<Theme> = {
    display: 'flex',
    textAlign: 'start !important',
    paddingTop: '10px !important',
    '.pricing-sub-heading': pricingSubHeadingSx
};

const pricingDurationWrapperSx: SxProps<Theme> = {
    '.pricing-duration': {
        fontSize: `${fontSize[30]} !important`,
        fontWeight: `${fontWeight.bold} !important`,
        color: `${colors.primary.main} !important`
    }
};

const Pricing = (props: any) => {
    const {heading, subheading, className, pricingData, onClick} = props;
    return (
        <MUIBox className={`margin pricing-size ${className} bottom-spacing mobile-top-margin mobile-bottom-padding`}>
            <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                    {heading}
                </Typography>
                <Typography variant="subtitle1" component="p" className="remove-link-underline font-regular-text">
                    {subheading ? parse(subheading) : ''}
                </Typography>
            </Box>
            <MUIBox>
                <MUIGrid container spacing={3} className="mobile-top-padding pricing-card-container-wrapper">
                    {pricingData?.map((item: any, index: number) => (
                        <MUIGrid item md={4} sm={6} xs={12} key={index}>
                            <MUIBox className="pricing-background" sx={pricingBackgroundSx}>
                                <MUIBox py={5} px={3}>
                                    <MUIBox className="spectrum-header-wrapper text-center head-heigth" sx={pricingHeadingWrapperSx}>
                                        <MUITypography className="spectrum-heading pricing-heading color-black ">
                                            {item.heading}
                                        </MUITypography>
                                        {item.subheading && (
                                            <p className="solution-sub-heading pricing-sub-heading color-black data_links hire-pricing-sub-heading">
                                                {item.subheading ? parse(item.subheading) : ''}
                                            </p>
                                        )}
                                    </MUIBox>
                                    {item?.points && (
                                        <MUIBox className="head-heigth-duration">
                                            {item?.points?.map((pointItem: string, priceIndex: number) => (
                                                <MUIBox
                                                    key={priceIndex}
                                                    className="spectrum-header-wrapper text-justify flex"
                                                    sx={pricingPointsWrapperSx}
                                                >
                                                    <DoneIcon className="icon-background" sx={iconBackgroundSx} />
                                                    <MUITypography className="solution-sub-heading pricing-sub-heading color-black">
                                                        {pointItem}
                                                    </MUITypography>
                                                </MUIBox>
                                            ))}
                                        </MUIBox>
                                    )}
                                    {item.duration && (
                                        <MUIBox className="spectrum-header-wrapper text-center top-spacing" sx={pricingDurationWrapperSx}>
                                            <MUITypography className="spectrum-heading pricing-duration color-black ">
                                                {item.duration}
                                            </MUITypography>
                                        </MUIBox>
                                    )}

                                    {item?.cta && (
                                        <MUIBox className="top-spacing text-center">
                                            <MUIButton
                                                className="pricing-button"
                                                sx={pricingButtonSx}
                                                variant="contained"
                                                label={item.cta}
                                                onClick={onClick}
                                            />
                                        </MUIBox>
                                    )}
                                </MUIBox>
                            </MUIBox>
                        </MUIGrid>
                    ))}
                </MUIGrid>
            </MUIBox>
        </MUIBox>
    );
};

export default memo(Pricing);
